import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CreditCard, Plus, Pencil, Trash2, Loader2, X, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPaymentMethodsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ country: '', bank_name: '', account_number: '', account_holder: '', method_type: 'transferencia', currency: 'USD', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/payment-methods`, { headers: getAuthHeaders() });
      setMethods(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ country: '', bank_name: '', account_number: '', account_holder: '', method_type: 'transferencia', currency: 'USD', notes: '' }); setEditing(null); setShowForm(false); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/payment-methods/${editing}`, form, { headers: getAuthHeaders() });
        toast.success('Metodo actualizado');
      } else {
        await axios.post(`${API}/payment-methods`, form, { headers: getAuthHeaders() });
        toast.success('Metodo creado');
      }
      resetForm();
      load();
    } catch (e) { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este metodo de pago?')) return;
    try {
      await axios.delete(`${API}/payment-methods/${id}`, { headers: getAuthHeaders() });
      toast.success('Eliminado');
      load();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${API}/payment-methods/${m.id}`, { is_active: !m.is_active }, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error('Error'); }
  };

  const startEdit = (m) => {
    setForm({ country: m.country, bank_name: m.bank_name, account_number: m.account_number, account_holder: m.account_holder, method_type: m.method_type, currency: m.currency, notes: m.notes || '' });
    setEditing(m.id);
    setShowForm(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const methodTypes = ['transferencia', 'deposito', 'zelle', 'pago_movil', 'efectivo'];

  return (
    <div className="space-y-6" data-testid="admin-payment-methods-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Metodos de Pago</h1>
          <p className="text-slate-500">Configura los metodos disponibles para clientes</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="add-payment-method-btn">
          <Plus className="w-4 h-4 mr-2" /> Agregar
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border-blue-200 bg-blue-50" data-testid="payment-method-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">{editing ? 'Editar' : 'Nuevo'} Metodo de Pago</h3>
            <button onClick={resetForm}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={handleSave} className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pais</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Venezuela" required data-testid="pm-country" />
            </div>
            <div className="space-y-2">
              <Label>Banco</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Banco de Venezuela" required data-testid="pm-bank" />
            </div>
            <div className="space-y-2">
              <Label>Numero de cuenta</Label>
              <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="0102-1234-5678" required data-testid="pm-account" />
            </div>
            <div className="space-y-2">
              <Label>Titular</Label>
              <Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} placeholder="Nombre del titular" required data-testid="pm-holder" />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <select value={form.method_type} onChange={(e) => setForm({ ...form, method_type: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="pm-type">
                {methodTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Moneda</Label>
              <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} placeholder="USD" data-testid="pm-currency" />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Notas (opcional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Instrucciones adicionales" data-testid="pm-notes" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="pm-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {methods.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <CreditCard className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay metodos de pago configurados</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {methods.map((m) => (
            <Card key={m.id} className={`p-4 border-0 shadow-sm ${!m.is_active ? 'opacity-50' : ''}`} data-testid={`pm-${m.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-900">{m.bank_name}</h3>
                    <span className="text-xs px-2 py-0.5 bg-slate-100 rounded-full text-slate-600">{m.country}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">Cuenta: {m.account_number} | Titular: {m.account_holder}</p>
                  <p className="text-xs text-slate-500 mt-1">Tipo: {m.method_type} | Moneda: {m.currency}</p>
                  {m.notes && <p className="text-xs text-slate-400 mt-1">{m.notes}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(m)} className="p-1" title={m.is_active ? 'Desactivar' : 'Activar'}>
                    {m.is_active ? <ToggleRight className="w-6 h-6 text-green-500" /> : <ToggleLeft className="w-6 h-6 text-slate-400" />}
                  </button>
                  <button onClick={() => startEdit(m)} className="p-1 text-blue-500 hover:text-blue-700"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(m.id)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPaymentMethodsPage;
